import type React from 'react';
import { useContext } from 'react';

import { Box, Button, Form, FormActions, Paragraph, Tag, Text, TextButton } from '@zillow/constellation-10';
import { IconAwardRibbonFilled } from '@zillow/constellation-icons';
import { TransitionWrapper } from '@zillow/zrs-comet';

import { FreemiumContext } from '../../Freemium/context';
import { ModularListingContext } from '../../../containers/CreateListingPage/context';

import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import actions from '../../../containers/CreateListingPage/actions';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';

import ModularListingCSEvents from '../../../model/ClickstreamEvent/ModularListingCSEvents';

import RestrictedListingBanner from '../../RestrictedListingBanner';

import { StyledContentContainer, StyledDesktopContainer, StyledHeaderContainer, StyledMobileGrid } from './styles';

export type PageWizardProps = {
  children: React.ReactNode;
  description?: React.ReactNode;
  isTransitioning?: boolean;
  wizardTitle?: React.ReactNode;
  subtitle?: React.ReactNode;
  title?: string;
  progressStepper: React.ReactNode;
  totalSteps?: number | null;
  currentStepIndex?: number;
  currentStepKey?: string;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  exitCTALabel?: string;
  hideDesktopWizardTitle?: boolean;
  hideExitCTA?: boolean;
  nextButtonType?: string;
  nextLabel?: string;
  backLabel?: string;
  onBackClick?: () => void;
  onNextClick?: () => void;
  onNextDataEventId?: string;
  onExitTriggered?: (...args: Array<unknown>) => void;
  showDesktopViewBreakpoint?: 'md' | 'xl' | 'xxl';
  hideBackButton?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  hideStepIndex?: boolean;
  footerContent?: React.ReactNode;
  isNarrowLayout?: boolean;
  displayPremiumTopOfFunnelHeader?: boolean;
  footer?: React.ReactNode;
};

const PageWizard = ({
  children,
  description = '',
  isTransitioning = false,
  wizardTitle,
  subtitle = null,
  title = '',
  progressStepper,
  totalSteps = null,
  currentStepIndex,
  currentStepKey = '',
  isBackDisabled = false,
  isNextDisabled = false,
  isNextLoading = false,
  nextButtonType = 'primary',
  nextLabel = 'Next',
  backLabel = 'Back',
  exitCTALabel = 'Exit',
  hideDesktopWizardTitle = false,
  hideExitCTA = false,
  onBackClick = () => {},
  onNextClick = () => {},
  onNextDataEventId,
  onExitTriggered = () => {},
  showDesktopViewBreakpoint = 'md',
  hideBackButton = false,
  hideHeader = false,
  hideFooter = false,
  hideStepIndex = false,
  footerContent = null,
  isNarrowLayout = false,
  displayPremiumTopOfFunnelHeader = false,
  footer = null,
}: PageWizardProps) => {
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const {
    properties: {
      currentProperty,
      currentProperty: {
        freemium: {
          activeProduct: { type: activeProductType },
        },
      },
    },
    user,
  } = useSelector((state) => state);

  const { freemiumState } = useContext(FreemiumContext);
  const { canAccessTopOfFunnelPremium } = freemiumState;
  const { reducerDispatch } = useContext(ModularListingContext);

  const displayPremiumPurchaseModal = (isOpen: boolean) => {
    actions.updateIsPremiumPurchaseModalOpen({
      reducerDispatch,
      isPremiumPurchaseModalOpen: isOpen,
    });

    ModularListingCSEvents.ClickUpgradeToPremiumFromHeader({
      currentProperty,
      user,
    });
  };

  const renderPremiumTopOfFunnelHeader = () => {
    return (
      <Box>
        <StyledHeaderContainer showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
          <Box css={{ display: { base: 'inline', md: 'flex' }, alignItems: 'center' }}>
            {activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && (
              <Tag
                css={{ backgroundColor: 'legacy.coolGray.200', color: 'blue.400', mdDown: { position: 'absolute' } }}
                icon={<IconAwardRibbonFilled />}
              >
                Premium
              </Tag>
            )}
            <Box
              css={{
                visibility:
                  hideDesktopWizardTitle || (isScreenMobile && activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM)
                    ? 'hidden'
                    : 'visible',
              }}
            >
              <Text textStyle="heading-xs" css={{ marginInline: '400' }} data-testid="desktop-wizard-title" asChild>
                <span>{wizardTitle}</span>
              </Text>
              {!hideStepIndex && currentStepIndex && totalSteps && totalSteps > 1 && (
                <Text css={{ display: { mdDown: 'none' } }} asChild>
                  <span>{`Step ${currentStepIndex} of ${totalSteps}`}</span>
                </Text>
              )}
            </Box>
          </Box>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {wizardTitle !== 'Publish' && activeProductType !== PRODUCT_TYPE_NAME_TO_ID.PREMIUM && (
              <TextButton
                onClick={() => displayPremiumPurchaseModal(true)}
                css={{ marginRight: '800', display: { base: 'none', lg: 'inherit' } }}
                data-eventid={ModularListingCSEvents.ClickUpgradeToPremiumFromHeader.data_event_id}
                icon={<IconAwardRibbonFilled />}
                iconPosition="end"
              >
                Upgrade listing to premium
              </TextButton>
            )}
            <Box css={{ visibility: hideExitCTA ? 'hidden' : 'visible' }}>
              <Button
                tone="neutral"
                emphasis="outlined"
                type="button"
                onClick={onExitTriggered}
                data-testid="desktop-exit-button"
              >
                {exitCTALabel}
              </Button>
            </Box>
          </Box>
        </StyledHeaderContainer>
        {progressStepper && (
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', alignItems: 'center' }}>
            <StyledMobileGrid showDesktopViewBreakpoint={showDesktopViewBreakpoint}>{progressStepper}</StyledMobileGrid>
          </Box>
        )}
      </Box>
    );
  };

  const renderHeader = () => {
    if (canAccessTopOfFunnelPremium && displayPremiumTopOfFunnelHeader) {
      return renderPremiumTopOfFunnelHeader();
    }

    return (
      <Box>
        <StyledHeaderContainer showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
          <Box css={{ visibility: hideDesktopWizardTitle ? 'hidden' : 'visible' }}>
            <Text textStyle="heading-xs" css={{ marginInline: '400' }} data-testid="desktop-wizard-title" asChild>
              <span>{wizardTitle}</span>
            </Text>
            {!hideStepIndex && currentStepIndex && totalSteps && totalSteps > 1 && (
              <Text css={{ display: { mdDown: 'none' } }} asChild>
                <span>{`Step ${currentStepIndex} of ${totalSteps}`}</span>
              </Text>
            )}
          </Box>
          <Box css={{ visibility: hideExitCTA ? 'hidden' : 'visible' }}>
            <Button
              tone="neutral"
              emphasis="outlined"
              type="button"
              onClick={onExitTriggered}
              data-testid="desktop-exit-button"
            >
              {exitCTALabel}
            </Button>
          </Box>
        </StyledHeaderContainer>
        {progressStepper && (
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', alignItems: 'center' }}>
            <StyledMobileGrid showDesktopViewBreakpoint={showDesktopViewBreakpoint}>{progressStepper}</StyledMobileGrid>
          </Box>
        )}
      </Box>
    );
  };

  const renderContent = () => (
    <>
      {title && (
        <Paragraph
          data-testid="title"
          css={{
            textStyle: { base: 'heading-xs', lg: 'heading-md' },
            marginBottom: '400',
          }}
        >
          {title}
        </Paragraph>
      )}
      {subtitle && (
        <Paragraph textStyle="body" data-testid="subtitle" css={{ marginBottom: '400' }}>
          {subtitle}
        </Paragraph>
      )}
      {description && (
        <Paragraph textStyle="body" data-testid="description">
          {description}
        </Paragraph>
      )}
      {children}
    </>
  );

  const renderFooter = () => {
    if (footer) {
      return footer;
    }

    return (
      <FormActions
        data-testid="PageWizard-Footer"
        css={{
          marginBlockStart: '0px !important',
          lgDown: {
            borderTop: '1px solid {colors.border.subtle}',
            borderBottom: '1px solid {colors.border.subtle}',
            position: 'fixed',
            width: '100%',
            bottom: 0,
            background: 'white',
            zIndex: '1',
          },
        }}
      >
        <Box css={{ marginInline: '600', marginBottom: '600' }}>{footerContent}</Box>

        <Box
          css={{
            display: 'flex',
            justifyContent: hideBackButton ? 'flex-end' : 'space-between',
            marginBlock: { base: '400', md: '600' },
            marginInline: { base: '400', md: '600', xl: '104px' },
          }}
        >
          {!hideBackButton && (
            <Button
              data-testid="back-button"
              fluid={true}
              disabled={isBackDisabled}
              onClick={onBackClick}
              type="button"
              tone="brand"
              emphasis="outlined"
              css={{
                flexGrow: 1,
                lg: {
                  maxWidth: '160px',
                  flexGrow: 0,
                  marginLeft: 0,
                },
              }}
            >
              {backLabel}
            </Button>
          )}

          <Button
            tone={nextButtonType === 'primary' ? 'brand' : 'neutral'}
            emphasis={nextButtonType === 'primary' ? 'filled' : 'outlined'}
            fluid={true}
            onClick={onNextClick}
            disabled={isNextDisabled}
            data-testid="next-button"
            type="submit"
            loading={isNextLoading}
            data-eventid={onNextDataEventId}
            css={{
              flexGrow: 1,
              marginLeft: '400',
              lg: {
                maxWidth: '160px',
                flexGrow: 0,
                marginLeft: 0,
              },
            }}
          >
            {nextLabel}
          </Button>
        </Box>
      </FormActions>
    );
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        width: '100%',
        md: { height: '100vh' },
      }}
    >
      <RestrictedListingBanner />
      {!hideHeader && renderHeader()}
      {isTransitioning ? (
        <TransitionWrapper isTransitioning={true} />
      ) : (
        <Form
          asChild={true}
          onSubmit={(e: React.FormEvent) => e.preventDefault()}
          css={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}
        >
          <div>
            <StyledContentContainer hideFooter={hideFooter}>
              {progressStepper && (
                <StyledDesktopContainer showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
                  {progressStepper}
                </StyledDesktopContainer>
              )}
              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(12, 1fr)',
                  marginTop: { base: '800', xxl: '1200' },
                  marginBottom: { base: '80px' },
                }}
                className="content-grid"
              >
                <Box
                  className="grid-col-1"
                  css={{
                    gridColumnEnd: {
                      base: 'span 0',
                      lg: isNarrowLayout ? 'span 2' : 'span 1',
                    },
                  }}
                />
                <Box
                  className="grid-col-2"
                  css={{
                    gridColumnEnd: {
                      base: 'span 12',
                      lg: isNarrowLayout ? 'span 8' : 'span 10',
                    },
                    justifySelf: 'inherit',
                  }}
                >
                  {renderContent()}
                </Box>
                <Box
                  className="grid-col-3"
                  css={{
                    gridColumnEnd: {
                      base: 'span 0',
                      lg: isNarrowLayout ? 'span 2' : 'span 1',
                    },
                  }}
                />
              </Box>
            </StyledContentContainer>
            {!hideFooter && renderFooter()}
          </div>
        </Form>
      )}
    </Box>
  );
};

export default PageWizard;
